// 📄 frontend/src/services/push.ts
// ============================================================================
// RAVERS NETWORK - Клиентский сервис Web Push уведомлений (VAPID + PWA)
// Включает: кроссбраузерную подписку, автосброс бейджей и хуки фокуса для iOS/Android
// ============================================================================

export interface PushSubscriptionPayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

// Резервный публичный VAPID-ключ по умолчанию
const DEFAULT_VAPID_PUBLIC_KEY = "BJQQcUqWHwhOzuX9ZAots5sMbemlV5_LYX9iyfvR2nNqr6mtSCDoNPfOITYXjyUUMqiR6JYk-3CaQ2slHCzo6yU";

/**
 * Проверка поддержки Web Push в текущем браузере
 */
export function isPushSupported(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return 'serviceWorker' in navigator && ('PushManager' in window || 'Notification' in window);
}

/**
 * Проверка запуска PWA в автономном режиме (Standalone / Установлено на экран Домой)
 */
export function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

/**
 * Детекция устройств Apple iOS
 */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

/**
 * Надежный сброс бейджа (цифры на иконке) приложения для iOS 16.4+ и Android
 */
export async function clearAppBadge(): Promise<void> {
  if (typeof navigator === 'undefined') return;

  // 1. Прямой вызов Badging API на уровне браузерного окна
  if ('clearAppBadge' in navigator) {
    try {
      await (navigator as any).clearAppBadge();
    } catch (_) {}
  } else if ('setAppBadge' in navigator) {
    try {
      await (navigator as any).setAppBadge(0);
    } catch (_) {}
  }

  // 2. Отправка системного сигнала в Service Worker для сброса внутри его контекста
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    try {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_BADGE' });
    } catch (_) {}
  }
}

/**
 * Регистрация Service Worker и настройка автоматического сброса бейджей
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;

  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

    // Сбрасываем зависшие бейджи сразу при запуске приложения
    await clearAppBadge();

    // Слушатель возврата в приложение: когда пользователь разблокировал экран или открыл PWA
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          clearAppBadge();
        }
      });
    }

    // Слушатель фокуса окна браузера
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => {
        clearAppBadge();
      });
    }

    return reg;
  } catch (err) {
    console.error('❌ Ошибка регистрации Service Worker (/sw.js):', err);
    return null;
  }
}

/**
 * Получение текущего состояния разрешений уведомлений
 */
export function getPushPermissionState(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Проверка наличия активной подписки в браузерном PushManager
 */
export async function isPushSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;

  if (localStorage.getItem('push_subscribed') === 'true' && Notification.permission === 'granted') {
    return true;
  }

  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg || !reg.pushManager) return false;

    const sub = await reg.pushManager.getSubscription();
    const active = sub !== null;
    if (active) {
      localStorage.setItem('push_subscribed', 'true');
    }
    return active;
  } catch {
    return false;
  }
}

/**
 * Конвертация base64URL VAPID-ключа в Uint8Array для браузерного PushManager
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Запрос разрешений у пользователя на отправку уведомлений
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }

  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch {
    return new Promise((resolve) => {
      Notification.requestPermission((p) => resolve(p));
    });
  }
}

/**
 * Полная подписка на Web Push (VAPID + PWA) с сохранением на сервере Rust
 */
export async function subscribeToPush(
  serverVapidPublicKey?: string
): Promise<{ success: boolean; message?: string; status?: NotificationPermission }> {
  if (!isPushSupported()) {
    return { success: false, message: 'Браузер не поддерживает Push-уведомления.' };
  }

  if (isIosDevice() && !isStandaloneMode()) {
    return {
      success: false,
      message: 'На iOS добавьте сайт на экран «Домой» через меню «Поделиться».',
    };
  }

  try {
    // 1. Запрос разрешения у пользователя
    const permission = await requestNotificationPermission();

    if (permission === 'denied') {
      return {
        success: false,
        status: 'denied',
        message: 'Уведомления заблокированы в настройках браузера. Сбросьте разрешения в настройках сайта.',
      };
    }

    if (permission !== 'granted') {
      return {
        success: false,
        status: permission,
        message: 'Разрешение на уведомления не было предоставлено.',
      };
    }

    // 2. Получение актуального публичного VAPID-ключа
    let vapidKey = serverVapidPublicKey;
    if (!vapidKey || vapidKey.trim() === '') {
      try {
        const res = await fetch('/api/push/vapid-key', { credentials: 'include' });
        if (res.ok) {
          const json = await res.json();
          if (json.public_key && typeof json.public_key === 'string' && json.public_key.trim() !== '') {
            vapidKey = json.public_key.trim();
          }
        }
      } catch (err) {
        console.warn('⚠️ Не удалось забрать VAPID ключ с сервера, применен дефолтный:', err);
      }
    }

    if (!vapidKey || vapidKey.trim() === '') {
      vapidKey = DEFAULT_VAPID_PUBLIC_KEY;
    }

    // 3. Проверка готовности Service Worker
    let reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      reg = (await registerServiceWorker()) || undefined;
    }

    if (!reg) {
      throw new Error('Service Worker не запущен.');
    }

    if (reg.installing || reg.waiting) {
      await new Promise<void>((resolve) => {
        const sw = reg!.installing || reg!.waiting;
        if (!sw) return resolve();
        sw.addEventListener('statechange', () => {
          if (sw.state === 'activated') resolve();
        });
        setTimeout(resolve, 2000);
      });
    }

    const applicationServerKey = urlBase64ToUint8Array(vapidKey);

    // 4. Оформление подписки в браузере
    let subscription = await reg.pushManager.getSubscription();
    if (subscription) {
      try {
        await subscription.unsubscribe();
      } catch (_) {}
    }

    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as unknown as BufferSource,
    });

    const subJson = subscription.toJSON();
    const payload: PushSubscriptionPayload = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: subJson.keys?.p256dh || '',
        auth: subJson.keys?.auth || '',
      },
    };

    // 5. Сохранение подписки в хранилище сервера Rust
    const saveRes = await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!saveRes.ok) {
      const errText = await saveRes.text();
      throw new Error(`Ошибка сохранения подписки на сервере: HTTP ${saveRes.status} (${errText})`);
    }

    localStorage.setItem('push_subscribed', 'true');

    // Сбрасываем бейджи при успешной подписке
    await clearAppBadge();

    return {
      success: true,
      status: 'granted',
      message: 'Уведомления успешно включены!',
    };
  } catch (err: any) {
    console.error('❌ Ошибка оформления подписки:', err);
    return {
      success: false,
      message: err.message || 'Сбой подписки на уведомления',
    };
  }
}

/**
 * Отписка устройства от Push-уведомлений
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    localStorage.removeItem('push_subscribed');
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg || !reg.pushManager) return true;

    const sub = await reg.pushManager.getSubscription();
    if (!sub) return true;

    const endpoint = sub.endpoint;
    await sub.unsubscribe();

    await fetch('/api/push/unsubscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ endpoint }),
    }).catch(() => {});

    await clearAppBadge();

    return true;
  } catch {
    return false;
  }
}
